import { api } from "./api-client.js";
import { renderLogin } from "./pages/login.js";
import { renderLayout } from "./layout.js";
import { DEFAULT_ROUTE, resolveRoute } from "./router.js";
import { registerServiceWorker } from "./register-sw.js";
import { getCurrentUser, setCurrentUser } from "./session.js";

const root = document.getElementById("app")!;

interface SessionInfo {
  authenticated: boolean;
  username?: string;
  jatem_user_id?: string;
  role?: "ADMIN" | "USER";
}

// role=USER에게는 아예 숨기는 화면(04-일반사용자-계정-추가-설계서.md §3-1) — 메뉴에서 감춰도
// 주소창에 해시를 직접 쳐서 들어올 수 있으므로 여기서도 막는다(백엔드 API도 별도로 403 처리).
const ADMIN_ONLY_HASHES = new Set(["#/login-history", "#/users"]);

// 세션을 조회해 currentUser(session.ts)를 채운다. 로그인 성공 직후를 포함해 "로그인 상태가
// 바뀔 수 있는 모든 시점"에 반드시 이 함수를 거쳐야 role/username이 화면에 정확히 반영된다
// (04-일반사용자-계정-추가-설계서.md §5-1/§10-1 — showApp()을 직접 부르면 이 갱신을 건너뛴다).
async function checkAuth(): Promise<boolean> {
  try {
    const info = await api.get<SessionInfo>("/api/auth/session");
    if (info.authenticated && info.username && info.jatem_user_id && info.role) {
      setCurrentUser({ username: info.username, jatemUserId: info.jatem_user_id, role: info.role });
    } else {
      setCurrentUser(null);
    }
    return info.authenticated;
  } catch {
    setCurrentUser(null);
    return false;
  }
}

async function showApp(): Promise<void> {
  const hash = window.location.hash || "#/dashboard";
  const { hash: resolvedHash, renderer } = resolveRoute(hash);
  const user = getCurrentUser();
  if (resolvedHash !== window.location.hash) {
    window.location.hash = resolvedHash;
    return; // hashchange 리스너가 다시 그림
  }
  if (ADMIN_ONLY_HASHES.has(resolvedHash) && user?.role !== "ADMIN") {
    window.location.hash = DEFAULT_ROUTE;
    return;
  }

  const content = renderLayout(
    root,
    resolvedHash,
    () => {
      void boot();
    },
    user,
  );
  await renderer(content);
}

async function checkAuthAndRender(): Promise<void> {
  const authenticated = await checkAuth();
  if (!authenticated) {
    renderLogin(root, () => {
      window.location.hash = "#/dashboard";
      void checkAuthAndRender();
    });
    return;
  }
  await showApp();
}

async function boot(): Promise<void> {
  await checkAuthAndRender();
}

window.addEventListener("hashchange", () => {
  void checkAuthAndRender();
});

// 사용자 요청: 화면이 1분마다 저절로 최신 데이터를 다시 받아오게. 탭이 백그라운드에 있거나
// (document.hidden) 사용자가 입력창에 뭔가 입력 중이면(비밀번호 변경 폼 등) 건너뛴다 — 안 그러면
// 타이핑 중에 화면이 갈아엎여서 입력하던 값이 사라진다.
function isEditingFormField(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  return el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT";
}

const AUTO_REFRESH_INTERVAL_MS = 60_000;
setInterval(() => {
  if (document.hidden || isEditingFormField()) return;
  void checkAuthAndRender();
}, AUTO_REFRESH_INTERVAL_MS);

registerServiceWorker();
void boot();
